import { FormField, FormSection } from "../dtos";
import { ConditionRule, evaluateRule, ruleSupported } from "./conditions";

/**
 * §Rules (decision 2026-07-30: "validation is from the engine itself"): render the template's own
 * per-field `validate` rules live, instead of hardcoding any check in the CRM. The engine is the
 * authority — it re-judges these on save AND completion (form-schema.helper `invalidFields`) — this
 * mirror only surfaces the same verdict while the agent types, using the same config. When the WM
 * team adds a rule (e.g. the design's unit-price match on the payment plan), the CRM shows it with
 * zero front-end changes.
 *
 * Supported shape — the engine's full `FieldValidation` vocabulary (form-schema.helper.ts):
 *   {op: 'equals'|'not_equals'|'gt'|'gte'|'lt'|'lte',
 *    valueField?: '<Section>.<Label>', factor?: number, value?: number, message?: string,
 *    when?: ConditionRule}
 * §Rule-arrays (product ask, 2026-08-06): `validate` may also be an ARRAY of rules, each with an
 * optional `when` branch condition (the current template's "Loan Amount ≤ 60M for Commercial" /
 * "DP ≥ 20% for Ijara" vocabulary — `when.field` can be an upstream `@s<order>:` key, resolved from
 * the facts map the caller passes). A rule whose `when` doesn't match is skipped.
 */
interface FieldRule {
  op?: string;
  valueField?: string;
  factor?: number;
  value?: number;
  message?: string;
  when?: unknown;
}

const num = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

const keyOf = (section: FormSection, field: FormField): string =>
  field.k ?? field.ref ?? `${section.name}.${field.l}`;

const DEFAULT_MESSAGE: Record<string, (bound: number) => string> = {
  gte: (bound) => `Must be at least ${Math.round(bound).toLocaleString("en-US")}`,
  lte: (bound) => `Must be at most ${Math.round(bound).toLocaleString("en-US")}`,
  gt: (bound) => `Must be greater than ${Math.round(bound).toLocaleString("en-US")}`,
  lt: (bound) => `Must be less than ${Math.round(bound).toLocaleString("en-US")}`,
  equals: (bound) => `Must equal ${Math.round(bound).toLocaleString("en-US")}`,
  not_equals: (bound) => `Must not equal ${Math.round(bound).toLocaleString("en-US")}`,
};

const holds = (op: string, own: number, bound: number): boolean => {
  switch (op) {
    case "gte":
      return own >= bound;
    case "lte":
      return own <= bound;
    case "gt":
      return own > bound;
    case "lt":
      return own < bound;
    case "equals":
      return own === bound;
    case "not_equals":
      return own !== bound;
    default:
      return true;
  }
};

const ruleError = (
  rule: FieldRule,
  own: number | undefined,
  values: Record<string, unknown>
): string | null => {
  const op = rule.op;
  if (op === undefined || DEFAULT_MESSAGE[op] === undefined || own === undefined) return null;
  const reference =
    rule.valueField !== undefined ? num(values[rule.valueField]) : rule.value;
  if (reference === undefined) return null;
  const bound = rule.factor !== undefined ? reference * rule.factor : reference;
  if (holds(op, own, bound)) return null;
  return rule.message ?? DEFAULT_MESSAGE[op](bound);
};

/**
 * A rule's `when` branch: unset ⇒ always applies. Otherwise it is a full engine `ConditionRule`
 * (`all` / `any` / `not` plus the leaf ops), evaluated by the same evaluator the precondition gates
 * use — a composite branch used to be dropped, silently disabling the rule. A branch this client
 * cannot evaluate is still left to the engine rather than guessed at.
 */
const whenMatches = (
  when: FieldRule["when"],
  values: Record<string, unknown>
): boolean => {
  if (!when) return true;
  if (!ruleSupported(when as ConditionRule)) return false;
  return evaluateRule(when as ConditionRule, values);
};

/**
 * §DBR-parity (prototype): the values a rule SUGGESTS, not just enforces. A field the template bounds
 * at a factor of another — Payment Plan's `Down Payment ≥ 20% of the Cash Price` — follows its source
 * while the agent hasn't touched it; the first manual edit on the target stops the suggestion for
 * good (`touched`), the same "manual wins" rule the derive engine applies.
 *
 * The factor is the template's own number: nothing here knows 20%. A rule with no factor is
 * enforcement only, and only the `gte` lower bound suggests — an upper bound says how high a value
 * may go, not what it should be. Rule ARRAYS are read like everywhere else (§Rule-arrays), so the
 * factor rule is found alongside authored caps.
 */
export const ruleFactorFills = (
  entries: { key: string; field: FormField }[],
  sourceKey: string,
  sourceValue: unknown,
  touched: Set<string>
): Record<string, number> => {
  const fills: Record<string, number> = {};
  const source = Number(sourceValue);
  if (!Number.isFinite(source) || source <= 0) return fills;
  for (const { key, field } of entries) {
    if (touched.has(key)) continue;
    const raw = field.validate as FieldRule | FieldRule[] | undefined;
    if (!raw) continue;
    const rule = (Array.isArray(raw) ? raw : [raw]).find(
      (candidate) =>
        candidate?.op === "gte" &&
        candidate.valueField === sourceKey &&
        typeof candidate.factor === "number"
    );
    if (rule) fills[key] = Math.round(source * (rule.factor as number));
  }
  return fills;
};

/** The `<Section>.<Label>`-keyed entries of a schema — the shape `ruleFactorFills` walks. */
export const fieldEntriesOf = (
  schema: FormSection[]
): { key: string; field: FormField }[] =>
  schema.flatMap((section) =>
    (section.fields ?? []).map((field) => ({ key: keyOf(section, field), field }))
  );

/** Evaluate every field's `validate` rule(s) against the live form values → {fieldKey: message}.
 *  `values` should carry the case facts too (upstream `@s…` keys) so branch conditions resolve. */
export const fieldRuleErrors = (
  schema: FormSection[],
  values: Record<string, unknown>
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const section of schema) {
    for (const field of section.fields ?? []) {
      const raw = field.validate as FieldRule | FieldRule[] | undefined;
      if (!raw) continue;
      const rules = Array.isArray(raw) ? raw : [raw];
      const key = keyOf(section, field);
      for (const rule of rules) {
        if (!rule?.op || !whenMatches(rule.when, values)) continue;
        const message = ruleError(rule, num(values[key]), values);
        if (message) {
          errors[key] = message;
          break; // first violated rule wins — one message per field
        }
      }
    }
  }
  return errors;
};
